import { useLocation } from 'react-router';

import { ResourceDoesNotExist } from '@/components/ErrorScreens/ResourceDoesNotExist';
import { Page } from '@/components/Page';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useTrackRecentlyViewed } from '@/hooks/admin/recentlyViewed';
import { useGenericResource } from '@/hooks/discovery/types';
import {
  getResourceNavOption,
  NavigationResourceType,
} from '@/panel/ResourcesFilter/selectedResource';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { ExposureView } from './components/DetailPages/ExposureView';
import { GroupView } from './components/DetailPages/GroupView';
import { MacroView } from './components/DetailPages/MacroView';
import { MetricView } from './components/DetailPages/MetricView';
import { ModelView } from './components/DetailPages/ModelView';
import { SavedQueryView } from './components/DetailPages/SavedQueryView';
import { SeedView } from './components/DetailPages/SeedView';
import { SemanticModelView } from './components/DetailPages/SemanticModelView';
import { SnapshotView } from './components/DetailPages/SnapshotView';
import { SourceView } from './components/DetailPages/SourceView';
import { TestView } from './components/DetailPages/TestView';

type DetailsPagesProps = {
  navigationResourceType: NavigationResourceType;
  uniqueId: ParsedUniqueId;
};

const ResourcesDetailsPages = ({
  navigationResourceType,
  uniqueId,
}: DetailsPagesProps) => {
  switch (navigationResourceType) {
    case 'model':
      return <ModelView uniqueId={uniqueId} />;
    case 'source':
      return <SourceView uniqueId={uniqueId} />;
    case 'macro':
      return <MacroView uniqueId={uniqueId} />;
    case 'snapshot':
      return <SnapshotView uniqueId={uniqueId} />;
    case 'exposure':
      return <ExposureView uniqueId={uniqueId} />;
    case 'seed':
      return <SeedView uniqueId={uniqueId} />;
    case 'test':
      return <TestView uniqueId={uniqueId} />;
    case 'group':
      return <GroupView uniqueId={uniqueId} />;
    case 'saved_query':
      return <SavedQueryView uniqueId={uniqueId} />;
    case 'semantic_model':
      return <SemanticModelView uniqueId={uniqueId} />;
    case 'metric':
      return <MetricView uniqueId={uniqueId} />;
    default:
      const exhaustiveCheck: never = navigationResourceType;
      throw new Error(`Unhandled navigation case: ${exhaustiveCheck}`);
  }
};

export const ResourceDetailsPage = () => {
  const { accountId, uniqueId, dbtUniqueId } = useDbtCloudProject();
  const navigationResourceType = getResourceNavOption(uniqueId?.resourceType);
  const resourceQuery = useGenericResource(uniqueId);
  const url = useLocation().pathname;

  useTrackRecentlyViewed(
    {
      accountId,
      url,
      name: resourceQuery.data?.name ?? '',
      description: resourceQuery.data?.description ?? '',
      resourceType: uniqueId?.resourceType,
    },
    resourceQuery.data === undefined,
  );

  let hasFetched = false;
  if (!resourceQuery.fetching && resourceQuery.data !== undefined) {
    hasFetched = true;
  }

  // if resource does not exist, `resourceQuery.data === {}`
  const resourceDoesNotExist =
    hasFetched && !Object.keys(resourceQuery.data ?? {}).length;

  if (!accountId) return null;
  if (!uniqueId || !navigationResourceType) {
    return <ResourceDoesNotExist uniqueId={dbtUniqueId || ''} />;
  }

  return (
    <Page title={uniqueId.remainder} pageType="resourceDetails">
      {resourceDoesNotExist && <ResourceDoesNotExist uniqueId={uniqueId.raw} />}
      {hasFetched && !resourceDoesNotExist && (
        <ResourcesDetailsPages
          navigationResourceType={navigationResourceType}
          uniqueId={uniqueId}
        />
      )}
    </Page>
  );
};
